import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera, ChevronLeft, CircleStop, Gauge, LogOut, RadioTower, Signal, SwitchCamera, Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { api, wsUrl } from "@/lib/api";
import { toast } from "sonner";

const LS_DEVICE_ID = "cam.device.id";
const LS_DEVICE_TOKEN = "cam.device.token";
const LS_PROFILE = "cam.profile";

// Quality/FPS profiles — user-facing broadcast presets. Kept intentionally
// small and predictable so we can iterate without touching consumers.
const PROFILES = {
  eco:      { label: "Éco",      fps: 3,  w: 480, h: 270, q: 0.50 },
  standard: { label: "Standard", fps: 6,  w: 640, h: 360, q: 0.60 },
  fluide:   { label: "Fluide",   fps: 12, w: 640, h: 360, q: 0.65 },
  max:      { label: "Max/Test", fps: 15, w: 640, h: 360, q: 0.70 },
};
const DEFAULT_PROFILE = "standard";

// Backpressure threshold: if the WS still has more than this many bytes in
// the send queue, drop the incoming frame rather than piling up latency.
const WS_BUFFER_LIMIT = 200_000;
const DROP_WARN_STREAK = 10;

function readProfile() {
  try {
    const v = localStorage.getItem(LS_PROFILE);
    if (v && PROFILES[v]) return v;
  } catch { /* noop */ }
  return DEFAULT_PROFILE;
}

export default function AgentPage() {
  const nav = useNavigate();
  const { logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [device, setDevice] = useState(null);
  const [token] = useState(() => {
    try { return localStorage.getItem(LS_DEVICE_TOKEN); } catch { return null; }
  });

  const [cameras, setCameras] = useState([]);
  const [selectedCam, setSelectedCam] = useState("");
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [fps, setFps] = useState(0);
  const [dropped, setDropped] = useState(0);
  const [lastSent, setLastSent] = useState(null);
  const [streamError, setStreamError] = useState(null);
  const [profileKey, setProfileKey] = useState(readProfile);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const wsRef = useRef(null);
  const wsAuthenticatedRef = useRef(false);
  const timerRef = useRef(null);
  const encodingRef = useRef(false);
  const dropStreakRef = useRef(0);
  const frameCountRef = useRef({ sent: 0, dropped: 0, windowStart: 0 });
  // profile ref stays in sync with state — the capture loop reads it live so
  // switching profile mid-broadcast picks up new fps/size/quality on the
  // next frame without tearing down the WS.
  const profileRef = useRef(PROFILES[readProfile()]);

  // ---------- Initial guard: device must exist and be active ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let deviceId = null;
      try { deviceId = localStorage.getItem(LS_DEVICE_ID); } catch { /* noop */ }
      if (!deviceId) {
        if (!cancelled) { setError("Aucun appareil enrôlé sur ce navigateur."); setLoading(false); }
        return;
      }
      try {
        const r = await api.get(`/devices/${deviceId}`);
        if (cancelled) return;
        if (r.data.approval_status !== "active") {
          setError(`Cet appareil n'est pas actif (statut : ${r.data.approval_status}).`);
        } else {
          setDevice(r.data);
        }
      } catch {
        if (!cancelled) setError("Impossible de récupérer l'état de cet appareil.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ---------- Enumerate cameras ----------
  const enumerateCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const vids = devices.filter((d) => d.kind === "videoinput");
      setCameras(vids);
      setSelectedCam((prev) => {
        if (prev && vids.some((v) => v.deviceId === prev)) return prev;
        // Prefer rear camera on mobile
        const rear = vids.find((v) => /back|rear|environment|arrière/i.test(v.label));
        return rear?.deviceId || vids[0]?.deviceId || "";
      });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { enumerateCameras(); }, [enumerateCameras]);

  // ---------- Camera lifecycle ----------
  const stopCameraTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch { /* noop */ }
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      try { videoRef.current.srcObject = null; } catch { /* noop */ }
    }
  }, []);

  const attachStream = useCallback(async (stream) => {
    streamRef.current = stream;
    const v = videoRef.current;
    if (v) {
      v.srcObject = stream;
      try { await v.play(); } catch { /* autoplay OK */ }
    }
  }, []);

  const requestUserMedia = useCallback(async (constraints) => {
    return await navigator.mediaDevices.getUserMedia({ audio: false, video: constraints });
  }, []);

  // Try to infer whether a camera label points at the front or the back
  // side of a mobile device. Used as a fallback when Android exposes
  // multiple back cameras but rejects a precise deviceId.
  const inferFacing = useCallback((camList, wantedId) => {
    const cam = camList.find((c) => c.deviceId === wantedId);
    const label = (cam?.label || "").toLowerCase();
    if (/front|user|selfie|avant|face/.test(label)) return "user";
    if (/back|rear|environment|arrière|world/.test(label)) return "environment";
    return null;
  }, []);

  const startCamera = useCallback(async (deviceId) => {
    // Always clear the previous error and the previous stream. The button
    // must feel responsive even after a failure.
    setStreamError(null);
    setCameraStarting(true);
    stopCameraTracks();
    // Android Chrome needs a beat between releasing a track and grabbing a
    // new one; without this the second getUserMedia() often returns
    // NotReadableError.
    await new Promise((r) => setTimeout(r, 150));

    const p = profileRef.current;
    // The profile is an envelope — request the LONG edge as an ideal hint
    // so the browser has room to return either landscape or portrait, and
    // we cap dimensions at capture time. Don't bias width vs height here.
    const longEdge = Math.max(p.w, p.h);
    const commonHints = { width: { ideal: longEdge }, height: { ideal: longEdge } };
    const facingHint = deviceId ? inferFacing(cameras, deviceId) : "environment";

    const attempts = [];
    if (deviceId) {
      attempts.push({ deviceId: { exact: deviceId }, ...commonHints });
      // Combined exact-device + facing: some Android builds honour the
      // facing constraint even when deviceId is stale.
      if (facingHint) {
        attempts.push({ deviceId: { ideal: deviceId }, facingMode: { ideal: facingHint }, ...commonHints });
        attempts.push({ facingMode: { exact: facingHint }, ...commonHints });
      }
    } else if (facingHint) {
      attempts.push({ facingMode: { ideal: facingHint }, ...commonHints });
    }
    // Final fallback: any camera.
    attempts.push({ ...commonHints });

    let lastError = null;
    for (const constraint of attempts) {
      try {
        const stream = await requestUserMedia(constraint);
        await attachStream(stream);
        setCameraOn(true);
        setCameraStarting(false);
        // Labels are only exposed after permission is granted — re-enumerate.
        await enumerateCameras();
        // If the browser silently returned a different physical camera,
        // sync the dropdown to reality and warn the user.
        try {
          const track = stream.getVideoTracks()[0];
          const settings = track?.getSettings?.() || {};
          if (settings.deviceId && settings.deviceId !== deviceId) {
            setSelectedCam(settings.deviceId);
            if (deviceId) {
              setStreamError(
                "Le navigateur a ouvert une autre caméra que celle demandée (contrainte matérielle).",
              );
            }
          }
        } catch { /* noop */ }
        return true;
      } catch (e) {
        lastError = e;
      }
    }
    // All attempts failed.
    setStreamError(mediaErrorMessage(lastError));
    setCameraOn(false);
    setCameraStarting(false);
    return false;
  }, [attachStream, cameras, enumerateCameras, inferFacing, requestUserMedia, stopCameraTracks]);

  const stopCamera = useCallback(() => {
    stopCameraTracks();
    setCameraOn(false);
  }, [stopCameraTracks]);

  // Attach stream ↔ video ref if the ref appears late
  useEffect(() => {
    if (!cameraOn) return;
    const v = videoRef.current;
    const s = streamRef.current;
    if (v && s && v.srcObject !== s) v.srcObject = s;
  }, [cameraOn]);

  // ---------- Broadcast loop ----------
  const scheduleCapture = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const intervalMs = Math.max(50, Math.round(1000 / profileRef.current.fps));
    timerRef.current = setInterval(() => {
      const v = videoRef.current;
      const c = canvasRef.current;
      const w = wsRef.current;
      if (!v || !c || !w) return;
      if (w.readyState !== WebSocket.OPEN || !wsAuthenticatedRef.current) return;
      if (!v.videoWidth) return;
      if (encodingRef.current) {
        // still encoding the previous frame — drop this tick
        frameCountRef.current.dropped += 1;
        dropStreakRef.current += 1;
        if (dropStreakRef.current === DROP_WARN_STREAK) {
          toast.warning("Encodage lent — frames abandonnées. Essayez un profil plus léger.");
        }
        return;
      }
      if (w.bufferedAmount > WS_BUFFER_LIMIT) {
        frameCountRef.current.dropped += 1;
        dropStreakRef.current += 1;
        return;
      }
      const p = profileRef.current;
      // Preserve the source aspect ratio. The profile's (w, h) act as a
      // pixel budget — we cap the LONGEST edge to max(w, h) and let the
      // shorter edge follow the natural ratio, so a portrait phone stream
      // (e.g. 720x1280) becomes a portrait JPEG (e.g. 360x640) instead of
      // being squashed into a fixed 640x360 landscape frame.
      const longEdge = Math.max(p.w, p.h);
      const src = Math.max(v.videoWidth, v.videoHeight);
      const scale = src > 0 ? Math.min(1, longEdge / src) : 1;
      const targetW = Math.max(2, Math.round(v.videoWidth * scale));
      const targetH = Math.max(2, Math.round(v.videoHeight * scale));
      c.width = targetW;
      c.height = targetH;
      const ctx = c.getContext("2d");
      try {
        ctx.drawImage(v, 0, 0, targetW, targetH);
      } catch { return; }
      encodingRef.current = true;
      c.toBlob((blob) => {
        encodingRef.current = false;
        if (!blob) return;
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN || !wsAuthenticatedRef.current) return;
        blob.arrayBuffer().then((buf) => {
          try {
            ws.send(buf);
            dropStreakRef.current = 0;
            frameCountRef.current.sent += 1;
            setLastSent(new Date());
            const now = performance.now();
            const elapsed = (now - frameCountRef.current.windowStart) / 1000;
            if (elapsed >= 1.0) {
              setFps(Math.round(frameCountRef.current.sent / elapsed));
              setDropped((d) => d + frameCountRef.current.dropped);
              frameCountRef.current = { sent: 0, dropped: 0, windowStart: now };
            }
          } catch { /* noop */ }
        }).catch(() => { /* noop */ });
      }, "image/jpeg", profileRef.current.q);
    }, intervalMs);
  }, []);

  const startBroadcast = useCallback(() => {
    if (!device || !token) { setStreamError("Contexte device manquant."); return; }
    if (!streamRef.current) { setStreamError("Démarrez d'abord la caméra."); return; }

    setStreamError(null);
    wsAuthenticatedRef.current = false;
    const ws = new WebSocket(wsUrl("/agents/ws"));
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: "auth",
        device_id: device.id,
        token,
      }));
    };
    ws.onmessage = (event) => {
      if (typeof event.data !== "string") return;
      let payload;
      try { payload = JSON.parse(event.data); } catch { return; }

      if (payload?.type === "welcome" && !wsAuthenticatedRef.current) {
        wsAuthenticatedRef.current = true;
        setWsConnected(true);
        ws.send(JSON.stringify({
          type: "hello",
          protocol: "1.0",
          agent_version: "browser-v1",
          capabilities: [{ kind: "video", subkind: "webcam", label: "Caméra navigateur", enabled: true }],
          network: {},
        }));
        scheduleCapture();
      } else if (payload?.type === "error" && payload?.error === "unauthorized") {
        setStreamError("Authentification de l'appareil refusée.");
        try { ws.close(); } catch { /* noop */ }
      }
    };
    ws.onclose = () => {
      wsAuthenticatedRef.current = false;
      setWsConnected(false);
      setBroadcasting(false);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
    ws.onerror = () => setStreamError("Erreur WebSocket.");

    setBroadcasting(true);
    setDropped(0);
    frameCountRef.current = { sent: 0, dropped: 0, windowStart: performance.now() };
    dropStreakRef.current = 0;
  }, [device, token, scheduleCapture]);

  const stopBroadcast = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    wsAuthenticatedRef.current = false;
    if (wsRef.current) {
      try { wsRef.current.close(); } catch { /* noop */ }
      wsRef.current = null;
    }
    setBroadcasting(false);
    setWsConnected(false);
    setFps(0);
    encodingRef.current = false;
  }, []);

  // Clean up on unmount / navigation
  useEffect(() => {
    const onLeave = () => { stopBroadcast(); stopCamera(); };
    window.addEventListener("pagehide", onLeave);
    return () => {
      window.removeEventListener("pagehide", onLeave);
      stopBroadcast();
      stopCamera();
    };
  }, [stopBroadcast, stopCamera]);

  // React to profile changes — rebuild the interval and persist choice.
  useEffect(() => {
    profileRef.current = PROFILES[profileKey] || PROFILES[DEFAULT_PROFILE];
    try { localStorage.setItem(LS_PROFILE, profileKey); } catch { /* noop */ }
    if (broadcasting) scheduleCapture();
  }, [profileKey, broadcasting, scheduleCapture]);

  const onCameraChange = async (id) => {
    if (id === selectedCam) return;
    const previousCam = selectedCam;
    setSelectedCam(id);
    // If broadcasting, pause the loop while we swap the source. The WS stays
    // open, so admins see a brief "connecting..." then frames resume.
    const wasBroadcasting = broadcasting;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const ok = await startCamera(id);
    if (ok && wasBroadcasting && wsRef.current?.readyState === WebSocket.OPEN) {
      scheduleCapture();
    } else if (!ok) {
      // Revert the dropdown to the previous working camera so we don't
      // leave the user staring at a selection that isn't actually active.
      setSelectedCam(previousCam);
      // Try to bring the previous camera back so the preview isn't blank.
      if (previousCam) {
        const revived = await startCamera(previousCam);
        if (revived && wasBroadcasting && wsRef.current?.readyState === WebSocket.OPEN) {
          scheduleCapture();
        } else if (!revived) {
          setStreamError(
            "Impossible d'ouvrir la caméra sélectionnée ni de restaurer la précédente. Vérifiez les permissions.",
          );
        }
      }
    }
  };

  const handleLogout = async () => {
    stopBroadcast(); stopCamera();
    await logout();
    nav("/login", { replace: true });
  };

  const currentProfile = PROFILES[profileKey] || PROFILES[DEFAULT_PROFILE];

  return (
    <div className="min-h-screen bg-obsidian grid-bg flex flex-col" data-testid="agent-page">
      {/* Top bar */}
      <div className="border-b border-edge px-4 h-14 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="outline" size="sm" onClick={() => nav("/select")} className="rounded-none border-edge bg-transparent" data-testid="agent-back">
            <ChevronLeft className="size-3.5 mr-1.5" strokeWidth={1.5} /> SÉLECTION
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <RadioTower className={`size-4 shrink-0 ${broadcasting ? "text-status-online" : "text-zinc-500"}`} strokeWidth={1.5} />
            <span className="font-display font-bold tracking-tight truncate">Caméra de surveillance</span>
          </div>
        </div>
        <button onClick={handleLogout} data-testid="agent-logout"
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-edge hover:border-status-offline hover:text-status-offline text-zinc-400 transition-colors">
          <LogOut className="size-3.5" strokeWidth={1.5} />
          <span className="hidden sm:inline">DÉCONNEXION</span>
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 flex items-center justify-center p-4">
        {loading ? (
          <div className="overline animate-pulse" data-testid="agent-loading">CHARGEMENT...</div>
        ) : error ? (
          <div className="panel rounded-none p-8 max-w-md text-center" data-testid="agent-error">
            <div className="overline text-status-offline mb-2">APPAREIL NON PRÊT</div>
            <p className="text-sm text-zinc-400 mb-6">{error}</p>
            <Button onClick={() => nav("/select")} className="rounded-none bg-brand hover:bg-brand-hover">RETOUR SÉLECTION</Button>
          </div>
        ) : (
          <div className="w-full max-w-3xl panel rounded-none" data-testid="agent-ready">
            <div className="border-b border-edge px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className={`size-1.5 rounded-full ${broadcasting ? "bg-status-online animate-pulse" : "bg-zinc-600"}`} />
                <span className="overline">{broadcasting ? "DIFFUSION ACTIVE" : "PRÊT"}</span>
              </div>
              <span className="text-[10px] font-mono text-zinc-500" data-testid="agent-device-name">{device?.name}</span>
            </div>

            <div className="relative aspect-video bg-black">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-contain bg-black" data-testid="agent-video" />
              <canvas ref={canvasRef} className="hidden" />
              {!cameraOn && (
                <div className="absolute inset-0 grid-bg scanlines flex items-center justify-center">
                  <div className="text-center px-6">
                    <Video className="size-10 mx-auto mb-3 text-zinc-600" strokeWidth={1} />
                    <div className="overline mb-2">
                      {cameraStarting ? "OUVERTURE CAMÉRA..." : "CAMÉRA ÉTEINTE"}
                    </div>
                    <Button
                      onClick={() => startCamera(selectedCam)}
                      disabled={cameraStarting}
                      className="rounded-none bg-brand hover:bg-brand-hover disabled:opacity-60"
                      data-testid="agent-start-camera">
                      <Camera className="size-4 mr-2" strokeWidth={2} /> AUTORISER LA CAMÉRA
                    </Button>
                  </div>
                </div>
              )}
              {streamError && (
                <div className="absolute bottom-0 left-0 right-0 bg-status-offline/90 text-white text-[11px] font-mono px-3 py-1.5" data-testid="agent-error-msg">
                  {streamError}
                </div>
              )}
            </div>

            {/* Metrics grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-edge border-b border-edge">
              <Metric label="CAMÉRA" value={cameraOn ? "AUTORISÉE" : "—"} testid="agent-metric-camera" ok={cameraOn} />
              <Metric label="AGENT" value={wsConnected ? "CONNECTÉ" : "—"} testid="agent-metric-ws" ok={wsConnected} />
              <Metric label="FPS" value={broadcasting ? `${fps} / ${currentProfile.fps}` : "—"} testid="agent-metric-fps" />
              <Metric label="DROPPED" value={broadcasting ? String(dropped) : "—"} testid="agent-metric-dropped"
                warn={dropped > 20} />
              <Metric label="DERNIÈRE FRAME" value={lastSent ? lastSent.toISOString().slice(11, 19) : "—"} testid="agent-metric-last" />
            </div>

            <div className="px-5 py-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                {cameras.length > 1 && (
                  <div className="flex items-center gap-2 min-w-0">
                    <SwitchCamera className="size-3.5 text-zinc-500 shrink-0" strokeWidth={1.5} />
                    <Select value={selectedCam} onValueChange={onCameraChange}>
                      <SelectTrigger className="rounded-none bg-obsidian border-edge text-xs h-8 min-w-[180px]" data-testid="agent-camera-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-panel border-edge">
                        {cameras.map((c) => (
                          <SelectItem key={c.deviceId} value={c.deviceId} data-testid={`agent-camera-${c.deviceId.slice(0, 8)}`}>
                            {c.label || `Caméra ${c.deviceId.slice(0, 6)}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex items-center gap-2 min-w-0">
                  <Gauge className="size-3.5 text-zinc-500 shrink-0" strokeWidth={1.5} />
                  <Select value={profileKey} onValueChange={setProfileKey}>
                    <SelectTrigger className="rounded-none bg-obsidian border-edge text-xs h-8 min-w-[160px]" data-testid="agent-profile-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-panel border-edge">
                      {Object.entries(PROFILES).map(([k, p]) => (
                        <SelectItem key={k} value={k} data-testid={`agent-profile-${k}`}>
                          {p.label} · {p.fps} FPS · ≤{Math.max(p.w, p.h)}px
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2">
                {!broadcasting ? (
                  <Button onClick={startBroadcast} disabled={!cameraOn}
                    className="rounded-none bg-status-online hover:bg-status-online/80 text-white disabled:opacity-50"
                    data-testid="agent-start-broadcast">
                    <RadioTower className="size-4 mr-2" strokeWidth={2} /> DÉMARRER LA DIFFUSION
                  </Button>
                ) : (
                  <Button onClick={stopBroadcast} variant="outline"
                    className="rounded-none border-status-offline/40 text-status-offline bg-transparent hover:bg-status-offline/10"
                    data-testid="agent-stop-broadcast">
                    <CircleStop className="size-4 mr-2" strokeWidth={2} /> ARRÊTER
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-edge px-6 py-3 text-[10px] font-mono text-zinc-600 text-center flex items-center justify-center gap-2">
        <Signal className="size-3" strokeWidth={1.5} />
        CAM · MODE APPAREIL · PROFIL {currentProfile.label.toUpperCase()} · JUSQU'À {Math.max(currentProfile.w, currentProfile.h)}px @ {currentProfile.fps} FPS · JPEG {Math.round(currentProfile.q * 100)}%
      </div>
    </div>
  );
}

function Metric({ label, value, testid, ok, warn }) {
  const tone = warn ? "text-status-pending" : ok ? "text-status-online" : "text-zinc-300";
  return (
    <div className="px-4 py-2.5">
      <div className="overline text-zinc-500">{label}</div>
      <div className={`mt-1 text-sm font-mono truncate ${tone}`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

function mediaErrorMessage(e) {
  const name = e?.name || "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "Permission caméra refusée. Autorisez l'accès dans les réglages du navigateur.";
  }
  if (name === "NotReadableError") {
    return "Caméra déjà utilisée par une autre application. Fermez-la puis réessayez.";
  }
  if (name === "OverconstrainedError") {
    return "Caméra demandée indisponible sur cet appareil.";
  }
  if (name === "NotFoundError") {
    return "Aucune caméra détectée.";
  }
  return `Accès caméra impossible (${name || "erreur inconnue"}).`;
}
